import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { statsAPI, DashboardStats } from '../services/api';
import {
  BarChart3,
  ArrowLeft,
  Loader,
  FileText,
  CheckCircle,
  XCircle,
  TrendingUp,
  User,
} from 'lucide-react';

const MESES: Record<string, string> = {
  Jan: 'Ene', Feb: 'Feb', Mar: 'Mar', Apr: 'Abr', May: 'May', Jun: 'Jun',
  Jul: 'Jul', Aug: 'Ago', Sep: 'Sep', Oct: 'Oct', Nov: 'Nov', Dec: 'Dic',
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await statsAPI.getDashboard();
        setStats(data);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Error al cargar estadísticas.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center py-16">
        <div className="text-center">
          <Loader className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-700" />
          <p className="font-medium text-slate-600">Cargando estadísticas…</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="animate-fade-in space-y-6 pb-4">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate('/templates')} className="btn-secondary mb-6">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver
          </button>
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800 font-medium">{error || 'No se pudieron cargar las estadísticas.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const maxMes = Math.max(1, ...stats.por_mes.map((m) => m.cantidad));

  return (
    <div className="animate-fade-in space-y-6 pb-4">
      <div className="max-w-6xl mx-auto">
        <div className="page-hero mb-2">
          <button type="button" onClick={() => navigate('/templates')} className="btn-secondary mb-4 py-2 text-sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a plantillas
          </button>
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-700 text-white shadow-md">
              <BarChart3 className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
                Indicadores
              </p>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Estadísticas</h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-600 sm:text-base">
                Resumen ejecutivo de consentimientos y disentimientos.
              </p>
            </div>
          </div>
        </div>

        {/* Tarjetas totales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="card bg-white border border-gray-200 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total registros</p>
              <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
            </div>
          </div>
          <div className="card bg-white border border-gray-200 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Consentimientos</p>
              <p className="text-2xl font-bold text-green-700">{stats.consentimientos}</p>
            </div>
          </div>
          <div className="card bg-white border border-gray-200 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Disentimientos</p>
              <p className="text-2xl font-bold text-red-700">{stats.disentimientos}</p>
            </div>
          </div>
        </div>

        {/* Por mes */}
        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Últimos 12 meses
          </h2>
          <div className="flex items-end gap-2 h-32">
            {stats.por_mes.map((m, i) => (
              <div key={`${m.anio}-${m.mes}`} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-blue-500 rounded-t min-h-[4px] transition-all"
                  style={{ height: maxMes ? `${(m.cantidad / maxMes) * 80}px` : 0 }}
                  title={`${MESES[m.mes] || m.mes} ${m.anio}: ${m.cantidad}`}
                />
                <span className="text-xs text-gray-500">{MESES[m.mes] || m.mes}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Por plantilla */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Por plantilla
            </h2>
            {stats.por_plantilla.length === 0 ? (
              <p className="text-gray-500 text-sm">Sin datos</p>
            ) : (
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Plantilla</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {stats.por_plantilla.map((p) => (
                      <tr key={p.template_id}>
                        <td className="px-3 py-2 text-gray-800 truncate max-w-[200px]" title={p.template_title}>
                          {p.template_title}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">{p.cantidad}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Por usuario */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Por usuario que llenó
            </h2>
            {stats.por_usuario.length === 0 ? (
              <p className="text-gray-500 text-sm">Sin datos</p>
            ) : (
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Usuario</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Cantidad</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {stats.por_usuario.map((u) => (
                      <tr key={u.filled_by}>
                        <td className="px-3 py-2 text-gray-800">{u.filled_by}</td>
                        <td className="px-3 py-2 text-right font-medium">{u.cantidad}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
